"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _image = require("@site/pattern-library-core/image");
var _motionFunctions = require("@site/pattern-library-core/motion-functions");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _scrollPosition = _interopRequireDefault(require("../../../experiments/animation/motion-parallax/scroll-position"));
var _breakpoints = require("@site/pattern-library-utils/breakpoints");
var _inlineVideo = _interopRequireDefault(require("../../../elements/other/inline-video"));
/* eslint-disable complexity */

const StickyBackground = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "StickyBackground",
  class: "pl-s69n19f",
  vars: {
    "s69n19f-0": [({
      isSinglePanel
    }) => isSinglePanel ? 'relative' : 'sticky'],
    "s69n19f-1": [({
      isSinglePanel,
      navHeights
    }) => {
      if (isSinglePanel) {
        return `calc(100svh - (${navHeights?.mobile || 0}px + var(--primary-nav-height)))`;
      }
      return '100svh';
    }],
    "s69n19f-4": [({
      animationTrigger
    }) => animationTrigger ? '0' : '16px'],
    "s69n19f-6": [({
      animationTrigger,
      navHeights,
      isSinglePanel
    }) => {
      if (isSinglePanel) {
        return 'var(--inset-current-all)';
      }
      return animationTrigger ? '0' : `calc(${navHeights?.mobile || 0}px + 16px + var(--primary-nav-height))`;
    }],
    "s69n19f-8": [({
      insetTop,
      animationTrigger,
      isSinglePanel
    }) => {
      if (isSinglePanel) {
        return insetTop ? 'var(--inset-current-all)' : '0';
      }
      if (insetTop) {
        return animationTrigger ? '0' : '16px';
      }
      return '0';
    }],
    "s69n19f-10": [({
      isSinglePanel,
      navHeights
    }) => {
      if (isSinglePanel) {
        return `calc(100vh - (${navHeights?.desktop || 0}px + var(--primary-nav-height)))`;
      }
      return '100vh';
    }],
    "s69n19f-11": [({
      animationTrigger
    }) => animationTrigger ? '0' : '12px'],
    "s69n19f-13": [({
      animationTrigger,
      navHeights,
      isSinglePanel
    }) => {
      if (isSinglePanel) {
        return 'var(--inset-current-all)';
      }
      return animationTrigger ? '0' : `calc(${navHeights?.desktop || 0}px + 12px + var(--primary-nav-height))`;
    }],
    "s69n19f-15": [({
      insetTop,
      animationTrigger,
      isSinglePanel
    }) => {
      if (isSinglePanel) {
        return insetTop ? 'var(--inset-current-all)' : '0';
      }
      if (insetTop) {
        return animationTrigger ? '0' : '12px';
      }
      return '0';
    }],
    "s69n19f-17": [({
      isSinglePanel,
      navHeights
    }) => {
      if (isSinglePanel) {
        return `calc(100svh - (${navHeights?.desktop || 0}px + var(--primary-nav-height)))`;
      }
      return '100svh';
    }]
  }
});
const VideoContainer = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "VideoContainer",
  class: "pl-v1uq5dt9"
});
const ImageContainer = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "ImageContainer",
  class: "pl-ijxbu4f",
  vars: {
    "ijxbu4f-0": [({
      active
    }) => active ? 'scale(1.03)' : 'scale(1)']
  }
});
const MediaContainer = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "MediaContainer",
  class: "pl-m1uq73j2",
  vars: {
    "m1uq73j2-0": [({
      active
    }) => active ? 'scale(1.03)' : 'scale(1)']
  }
});

/**
 * StickyBackgroundSection Component.
 *
 * @param {Object} props React props
 * @param {string} props.introUrl Intro video URL
 * @param {string} props.introUrlWebm Intro video URL in WebM format
 * @param {string} props.introUrlLaptop Intro video URL for laptop
 * @param {string} props.introUrlLaptopWebm Intro video URL for laptop in WebM format
 * @param {string} props.thumbnailDesktop Desktop thumbnail URL
 * @param {string} props.thumbnailMobile Mobile thumbnail URL
 * @param {Element} props.videos React element with Bynder video props
 * @param {string} props.baseUrl Base video URL
 * @param {string} props.laptopUrl Laptop video URL
 * @param {string} props.basePoster Base video poster
 * @param {string} props.laptopPoster Laptop video poster
 * @param {string} props.preload Preload value for video
 * @param {Object} props.portrait Portrait image object
 * @param {Object} props.landscape Landscape image object
 * @param {boolean} props.insetTop Flag to inset top
 * @param {Object} props.navHeights Navigation heights object
 * @param {number} props.videoLoadDelay Delay before showing video
 * @param {boolean} props.isSinglePanel Flag to indicate single panel layout mode
 * @returns {React.ReactElement} StickyBackgroundSection Component
 * @public
 */
function StickyBackgroundSection({
  introUrl,
  introUrlWebm,
  introUrlLaptop,
  introUrlLaptopWebm,
  thumbnailDesktop,
  thumbnailMobile,
  videos,
  preload = 'metadata',
  portrait,
  landscape,
  insetTop = false,
  navHeights,
  isSinglePanel = false
}) {
  const {
    scrollY
  } = (0, _scrollPosition.default)();
  const hasVideo = introUrl || introUrlWebm || videos && /*#__PURE__*/_react.default.isValidElement(videos);
  const shouldRenderImage = portrait || landscape;
  const animationTrigger = scrollY > 0;
  return /*#__PURE__*/_react.default.createElement(StickyBackground, {
    className: "sticky-background-container",
    insetTop: insetTop,
    animationTrigger: animationTrigger,
    navHeights: navHeights,
    isSinglePanel: isSinglePanel
  }, hasVideo && /*#__PURE__*/_react.default.createElement(MediaContainer, {
    "data-cy": "inline-video",
    active: animationTrigger
  }, shouldRenderImage && /*#__PURE__*/_react.default.createElement(ImageContainer, {
    active: animationTrigger
  }, /*#__PURE__*/_react.default.createElement(_image.LazyBackgroundImage, {
    "data-cy": "marquee-image",
    portrait: portrait?.url,
    landscape: landscape?.url,
    imgLoading: "eager",
    breakOn: "mobile"
  })), /*#__PURE__*/_react.default.createElement(VideoContainer, {
    className: "video-container"
  }, /*#__PURE__*/_react.default.createElement(_inlineVideo.default, {
    introUrl: introUrl,
    introUrlWebm: introUrlWebm,
    introUrlLaptop: introUrlLaptop,
    introUrlLaptopWebm: introUrlLaptopWebm,
    thumbnailDesktop: thumbnailDesktop,
    thumbnailMobile: thumbnailMobile,
    videos: videos,
    preload: preload,
    loop: true,
    hidePlayButton: true
  }))), !hasVideo && shouldRenderImage && /*#__PURE__*/_react.default.createElement(ImageContainer, {
    active: animationTrigger
  }, /*#__PURE__*/_react.default.createElement(_image.LazyBackgroundImage, {
    "data-cy": "marquee-image",
    portrait: portrait?.url,
    landscape: landscape?.url,
    imgLoading: "eager",
    breakOn: "mobile"
  })));
}
var _default = exports.default = StickyBackgroundSection;
StickyBackgroundSection.propTypes = {
  transformY: _propTypes.default.number,
  introUrl: _propTypes.default.string,
  introUrlWebm: _propTypes.default.string,
  introUrlLaptop: _propTypes.default.string,
  introUrlLaptopWebm: _propTypes.default.string,
  thumbnailDesktop: _propTypes.default.string,
  thumbnailMobile: _propTypes.default.string,
  videos: _propTypes.default.node,
  baseUrl: _propTypes.default.string,
  laptopUrl: _propTypes.default.string,
  basePoster: _propTypes.default.string,
  laptopPoster: _propTypes.default.string,
  preload: _propTypes.default.string,
  portrait: _propTypes.default.object,
  landscape: _propTypes.default.object,
  insetTop: _propTypes.default.bool,
  navHeights: _propTypes.default.object,
  videoLoadDelay: _propTypes.default.number,
  isSinglePanel: _propTypes.default.bool
};